import Decimal from 'decimal.js';
import { Maybe } from 'graphql/jsutils/Maybe';
import { compact, sortBy, uniqBy } from 'lodash';

import { Node } from '@/components/diagrams/FlowChart';
import {
  isTextNoteId,
  textNoteNodeToConfig,
} from '@/components/diagrams/FlowChart/utils/textNoteUtils';
import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { LOCAL_STORAGE_KEYS } from '@/constants/localStorageKeys';
import {
  mostRecentlyViewedWaterfallVar,
  NewHypotheticalItemNotification,
  newHypotheticalItemNotificationVar,
} from '@/graphql/reactiveVars';
import {
  AfterDeath,
  AugmentedUpdateEstateWaterfallInput,
  BatchUpdateEstateWaterfallInput,
  ClientOrganizationKind,
  EntityGraphNodeConfigurationInput,
  EntityStage,
  EstateWaterfallEdgeKind,
  EstateWaterfallKind,
  EstateWaterfallTransferKind,
  TestamentaryEntityKind,
} from '@/types/schema';
import { assertExact } from '@/utils/assertUtils';
import { sumDecimalJS } from '@/utils/decimalJSUtils';
import { diagnostics } from '@/utils/diagnostics';
import { formatEnumCase } from '@/utils/formatting/strings';
import { getNodes } from '@/utils/graphqlUtils';

import { CHARITABLE_ENTITY_TYPES } from '../entities/entities.constants';
import {
  CHARITABLE_TESTAMENTARY_ENTITY_KINDS,
  testamentaryEntityKindToDisplayName,
} from '../entities/testamentaryEntities/testamentaryEntities.utils';
import {
  EntitySubtype,
  getEntityTypeFromSubtype,
} from '../entities/utils/getEntityTypeFromSubtype';
import { isNodeEligibleForGraveyard } from '../graphViz/graphVizNodeConfig/nodeGraveyard.utils';
import { EstateWaterfallHypotheticalCharitableTrustModalQuery } from './components/EstateWaterfallHypotheticalCharitableTrustModal/graphql/EstateWaterfallHypotheticalCharitableTrustModal.generated';
import { SourceValues } from './components/EstateWaterfallHypotheticalGratModal/EstateWaterfallHypotheticalGratModal.types';
import { EstateWaterfallHypotheticalGratModalQuery } from './components/EstateWaterfallHypotheticalGratModal/graphql/EstateWaterfallHypotheticalGratModal.generated';
import { UpdateEstateWaterfallMutationVariables } from './components/EstateWaterfallModal/graphql/EstateWaterfallModal.generated';
import {
  EstateWaterfall_GiftTaxSummaryFragment,
  EstateWaterfall_GraphVizGroupFragment,
  EstateWaterfall_NodeFragment,
  EstateWaterfall_TaxSummaryV2Fragment,
  EstateWaterfallFragment,
} from './graphql/EstateWaterfall.generated';
import { GetWaterfallSummary_EstateWaterfallVizNodeFragment } from './graphql/GetWaterfallSummary.generated';
import { isUIOnlyGroupNode } from './waterfallGraph/utils';

/**
 * Returns whether a tax amount should be shown in the waterfall.
 * formatCurrencyNoDecimals displays values below one dollar as $0.
 */
export function hasDisplayableTax(
  tax: Decimal | null | undefined
): tax is Decimal {
  return tax?.greaterThanOrEqualTo(1) ?? false;
}

export function getUpdateWaterfallVariables(
  waterfall: Pick<
    EstateWaterfallFragment,
    'id' | 'visualizationGroups' | 'visualizationConfig' | 'nodeConfigurations'
  >,
  nodes: Node[]
): UpdateEstateWaterfallMutationVariables {
  const newNodeConfigurations = nodes.flatMap((node) => {
    const {
      id: nodeID,
      position: { x: xPosition, y: yPosition },
      width,
      height,
      data,
      type,
    } = node;
    // Text note nodes are serialized separately
    if (type === 'textNote') {
      return textNoteNodeToConfig(node);
    }

    // We don't save section group info since it's computed by a separate process and never saved
    if (type !== 'tile') return [];

    // For graveyard nodes, only save if they have notes
    // This allows notes to persist even when tiles don't have position configurations yet
    if (isNodeEligibleForGraveyard(node)) {
      if (!data.notes) return [];

      const graveyardConfig: EntityGraphNodeConfigurationInput = {
        nodeID,
        xPosition: null,
        yPosition: null,
        hidden: false,
        ...(typeof data.notes === 'string' && { notes: data.notes }),
        ...(data.componentType && {
          type: data.componentType,
        }),
        ...(data.iconShape && { iconShape: data.iconShape }),
        ...(data.tileEmphasis && { tileEmphasis: data.tileEmphasis }),
      };
      return graveyardConfig;
    }

    return {
      nodeID,
      xPosition,
      yPosition,
      hidden: false,
      ...(height && { height }),
      ...(width && { width }),
      ...(data.fontSizeVariant && { fontSize: data.fontSizeVariant }),
      ...(typeof data.notes === 'string' &&
        data.notes && { notes: data.notes }),
      ...(data.componentType && {
        type: data.componentType,
      }),
      ...(data.iconShape && { iconShape: data.iconShape }),
      ...(data.tileEmphasis && { tileEmphasis: data.tileEmphasis }),
      ...(data.dimensionsFinalized && { dimensionsFinalized: true }),
      ...(data.estateWaterfallConfig && {
        estateWaterfallConfig: data.estateWaterfallConfig,
      }),
    };
  });

  const existingNodeConfigurations: EntityGraphNodeConfigurationInput[] =
    waterfall.nodeConfigurations?.flatMap((cfg) => {
      if (!cfg.notes) {
        return [];
      }
      // Text notes don't use the graveyard pattern — they're fully deleted
      // when removed from state.nodes. Re-injecting here would resurrect
      // orphan rows in the DB on every save after a delete.
      if (isTextNoteId(cfg.nodeID)) {
        return [];
      }

      // Clear the x and y positions so that the node will be placed in the node graveyard when it
      // is added back to the diagram
      return {
        ...cfg,
        xPosition: null,
        yPosition: null,
      };
    });

  // Prefer the new node configurations over the existing ones, but keep the existing ones around should we
  // add the nodes back to the diagram later through ungrouping
  const nodeConfigurations = uniqBy(
    [...newNodeConfigurations, ...existingNodeConfigurations],
    (c) => c.nodeID
  );

  const updateVisualizationGroups: AugmentedUpdateEstateWaterfallInput['updateVisualizationGroups'] =
    getNodes(waterfall.visualizationGroups).flatMap((group) => {
      if (isUIOnlyGroupNode(group.id)) return [];

      return {
        id: group.id,
        update: { nodeIds: group.nodes.map((n) => n.id) },
      };
    });

  const { __typename, id, ...nextVizConfig } = waterfall.visualizationConfig!;
  const updateVisualizationConfig: AugmentedUpdateEstateWaterfallInput['updateVisualizationConfig'] =
    { update: { ...nextVizConfig }, id };

  return assertExact<UpdateEstateWaterfallMutationVariables>()({
    input: {
      id: waterfall.id,
      update: { nodeConfigurations },
      updateVisualizationGroups,
      updateVisualizationConfig,
    },
  });
}

interface IsHypotheticalWaterfallInput {
  kind?: EstateWaterfallKind | null;
}

export function isHypotheticalWaterfall(
  waterfall?: IsHypotheticalWaterfallInput | null
) {
  return getWaterfallKind(waterfall) === EstateWaterfallKind.Hypothetical;
}

export function getWaterfallKind(
  waterfall?: IsHypotheticalWaterfallInput | null
): EstateWaterfallKind {
  return waterfall?.kind ?? EstateWaterfallKind.Base;
}

interface BaseWaterfallForDeathOrderInput extends IsHypotheticalWaterfallInput {
  id: string;
  createdAt: Date;
  firstGrantorDeath: { id: string };
}

/**
 * Returns the "base" waterfall for a given death order: the oldest
 * non-hypothetical waterfall whose first grantor death matches. When multiple
 * non-hypothetical waterfalls share the death order, the oldest is treated as
 * the base.
 */
export function getBaseWaterfallForDeathOrder<
  T extends BaseWaterfallForDeathOrderInput,
>(waterfalls: T[] | undefined, firstGrantorDeathId: string | null | undefined) {
  if (!firstGrantorDeathId) {
    return undefined;
  }

  const candidates = (waterfalls ?? []).filter(
    (wf) =>
      !isHypotheticalWaterfall(wf) &&
      wf.firstGrantorDeath.id === firstGrantorDeathId
  );

  return sortBy(candidates, (wf) => wf.createdAt.getTime())[0];
}

export function setMostRecentlyViewedWaterfallId(
  mostRecentlyViewedWaterfallId: string,
  householdId: string
) {
  const key = `${LOCAL_STORAGE_KEYS.LAST_VIEWED_WATERFALL}_${householdId}`;
  localStorage.setItem(key, mostRecentlyViewedWaterfallId);
  // Broadcast to mostRecentlyViewedWaterfallVar that the value has changed.
  // This will trigger refetch of all User.localMostRecentlyViewedWaterfall queries.
  mostRecentlyViewedWaterfallVar({
    id: mostRecentlyViewedWaterfallId,
  });
}

/**
 * Sets the notification status for the most recently created hypothetical item.
 * This will be used to show a notification to the user when a new hypothetical item is created.
 * @param entityId The ID of the entity associated with the hypothetical item
 * @param proposalId The ID of the proposal associated with the hypothetical item
 * @param dismissed Whether the notification has been dismissed by the user
 */
export function setNewHypotheticalItemNotification({
  entityId,
  proposalId,
  dismissed = false,
}: {
  entityId?: string;
  proposalId?: string;
  dismissed?: boolean;
}) {
  const notification: NewHypotheticalItemNotification = entityId
    ? { entityId, dismissed }
    : { proposalId: proposalId ?? '', dismissed };

  // Store in localStorage with a single key (no need to key by entity ID)
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.HYPOTHETICAL_ITEM_NOTIFICATION,
    JSON.stringify(notification)
  );

  // Update the reactive variable
  newHypotheticalItemNotificationVar(notification);
}

/**
 * Gets the notification status for the most recently created hypothetical item.
 * @returns The notification status, or null if no notification exists
 */
export function getNewHypotheticalItemNotification(): NewHypotheticalItemNotification | null {
  const storedValue = localStorage.getItem(
    LOCAL_STORAGE_KEYS.HYPOTHETICAL_ITEM_NOTIFICATION
  );

  if (!storedValue) {
    return null;
  }

  try {
    return JSON.parse(storedValue) as NewHypotheticalItemNotification;
  } catch (e) {
    // If parsing fails, remove the invalid value and return null
    localStorage.removeItem(LOCAL_STORAGE_KEYS.HYPOTHETICAL_ITEM_NOTIFICATION);
    return null;
  }
}

/**
 * Dismisses the notification for the most recently created hypothetical item.
 */
export function dismissNewHypotheticalItemNotification() {
  const notification = getNewHypotheticalItemNotification();

  if (notification) {
    const updatedNotification: NewHypotheticalItemNotification = {
      ...notification,
      dismissed: true,
    };

    localStorage.setItem(
      LOCAL_STORAGE_KEYS.HYPOTHETICAL_ITEM_NOTIFICATION,
      JSON.stringify(updatedNotification)
    );

    newHypotheticalItemNotificationVar(updatedNotification);
  }
}

/**
 * Undoes the dismissal of the notification for the most recently created hypothetical item.
 */
export function undoDismissNewHypotheticalItemNotification() {
  const notification = getNewHypotheticalItemNotification();

  if (notification) {
    const updatedNotification: NewHypotheticalItemNotification = {
      ...notification,
      dismissed: false,
    };

    localStorage.setItem(
      LOCAL_STORAGE_KEYS.HYPOTHETICAL_ITEM_NOTIFICATION,
      JSON.stringify(updatedNotification)
    );

    newHypotheticalItemNotificationVar(updatedNotification);
  }
}

type IsCharitableNodeInput = {
  __typename?: string;
  id: string;
  subtype?: {
    __typename?: EntitySubtype;
    displayName?: string;
  };
  clientOrganizationKind?: ClientOrganizationKind | null;
  testamentaryEntityKind?: TestamentaryEntityKind;
  stage?: EntityStage;
  cltProposal?: {
    id: string;
  } | null;
  crtProposal?: {
    id: string;
  } | null;
};

export function getIsCharitableNode(node: IsCharitableNodeInput): boolean {
  // DAFs, private foundations, and charitable trusts
  const isCharitableEntity =
    node.__typename === 'Entity' &&
    node.subtype?.__typename &&
    CHARITABLE_ENTITY_TYPES.includes(
      getEntityTypeFromSubtype(node.subtype.__typename)
    );

  const isCharitableProposal =
    node.__typename === 'Proposal' &&
    !!(node.cltProposal?.id || node.crtProposal?.id);

  // Charitable organizations
  const isCharitableClientOrg =
    node.__typename === 'ClientOrganization' &&
    node.clientOrganizationKind ===
      ClientOrganizationKind.CharitableOrganization;
  // Charitable testamentary trusts and charitable testamentary entities
  const isCharitableTestamentaryEntity =
    node.__typename === 'TestamentaryEntity' &&
    !!node.testamentaryEntityKind &&
    CHARITABLE_TESTAMENTARY_ENTITY_KINDS.includes(node.testamentaryEntityKind);

  return (
    isCharitableEntity ||
    isCharitableProposal ||
    isCharitableClientOrg ||
    isCharitableTestamentaryEntity
  );
}

type IsPouroverEdgeInput = {
  kind: EstateWaterfallEdgeKind;
  associatedTransferLogItems?: Maybe<{ kind: EstateWaterfallTransferKind }[]>;
};

/**
 * True when the edge moves assets under a pourover will, either because the
 * edge itself is a pourover or because it carries a pourover-will transfer in
 * its log. The waterfall diagram and the waterfall summary must agree on which
 * nodes a pourover touches, so both read it from here.
 */
export function getIsPouroverEdge(edge: IsPouroverEdgeInput): boolean {
  return (
    edge.kind === EstateWaterfallEdgeKind.Pourover ||
    !!edge.associatedTransferLogItems?.some(
      ({ kind }) => kind === EstateWaterfallTransferKind.PouroverWill
    )
  );
}

export function getIsDraftNode(node: EstateWaterfall_NodeFragment['node']) {
  if ('stage' in node) {
    return node.stage === EntityStage.Draft;
  }

  return false;
}

export function getTaxStateAndFederalAndGst(
  waterfallSummary: Maybe<
    | EstateWaterfall_TaxSummaryV2Fragment
    | EstateWaterfall_GiftTaxSummaryFragment
  >
): {
  federal: Decimal;
  state: Decimal;
  gst: Decimal;
  inheritanceTax: Decimal;
} {
  if (!waterfallSummary) {
    return {
      federal: new Decimal(0),
      state: new Decimal(0),
      gst: new Decimal(0),
      inheritanceTax: new Decimal(0),
    };
  }

  let federal: Decimal = new Decimal(0);

  if ('federalGiftTax' in waterfallSummary) {
    federal = waterfallSummary.federalGiftTax?.tax ?? new Decimal(0);
  } else if ('federalTax' in waterfallSummary) {
    federal = waterfallSummary.federalTax?.tax ?? new Decimal(0);
  }

  let state: Decimal = new Decimal(0);

  if ('stateTax' in waterfallSummary) {
    state = sumDecimalJS(
      compact(waterfallSummary?.stateTax?.map((t) => t.tax)) ?? []
    );
  } else if ('stateGiftTax' in waterfallSummary) {
    state = sumDecimalJS(
      compact(waterfallSummary?.stateGiftTax?.map((t) => t.tax)) ?? []
    );
  }

  let gst: Decimal = new Decimal(0);

  if ('gstGiftTax' in waterfallSummary) {
    gst = waterfallSummary.gstGiftTax?.tax ?? new Decimal(0);
  } else if ('gstTax' in waterfallSummary) {
    gst = waterfallSummary.gstTax?.tax ?? new Decimal(0);
  }

  let inheritanceTax: Decimal = new Decimal(0);

  if ('stateInheritanceTax' in waterfallSummary) {
    inheritanceTax = sumDecimalJS(
      compact(waterfallSummary?.stateInheritanceTax?.map((t) => t.tax)) ?? []
    );
  }

  return { federal, state, gst, inheritanceTax };
}

export function getCombinedTaxStateAndFederalAndGst(
  waterfallSummary: Maybe<
    | EstateWaterfall_TaxSummaryV2Fragment
    | EstateWaterfall_GiftTaxSummaryFragment
  >
) {
  const { federal, state, gst, inheritanceTax } =
    getTaxStateAndFederalAndGst(waterfallSummary);
  return federal.plus(state).plus(gst).plus(inheritanceTax);
}

type FilterableWaterfallNode = {
  id: string;
  afterDeath?: string;
  isHidden: boolean;
  node: {
    __typename?: string;
    stage?: EntityStage | null;
  };
};

type IdConnection = {
  edges?: ({ node?: { id: string } | null } | null)[] | null;
};

type FilterableWaterfall = {
  filter?: {
    includedEntities?: IdConnection | null;
    includedClients?: IdConnection | null;
  } | null;
  visualizationWithProjections: {
    nodes?: FilterableWaterfallNode[];
    edges?: {
      to: { id: string; afterDeath?: string };
      from: { id: string; afterDeath?: string };
    }[];
  };
  visualizationGroups?: {
    edges?:
      | ({
          node?: {
            nodes: {
              id: string;
            }[];
          } | null;
        } | null)[]
      | null;
  };
} | null;

/**
 * Returns a section-aware composite ID for orphan detection.
 * When afterDeath is available, nodes are tracked per-section so that
 * an entity can be orphaned in one section but visible in another.
 */
function getFilterNodeId(node: { id: string; afterDeath?: string }): string {
  return node.afterDeath ? `${node.afterDeath}:${node.id}` : node.id;
}

export function applyWaterfallFilter<T extends FilterableWaterfall>(
  waterfall: T
): T {
  if (!waterfall) {
    return waterfall;
  }

  const checkedEntityIds = new Set(
    getNodes(waterfall.filter?.includedEntities).map((e) => e.id)
  );
  const checkedClientIds = new Set(
    getNodes(waterfall.filter?.includedClients).map((c) => c.id)
  );
  const hasUserFilter = checkedEntityIds.size > 0 || checkedClientIds.size > 0;

  const visibleNodes =
    waterfall.visualizationWithProjections.nodes?.filter((n) => {
      if (n.isHidden) return false;
      if (
        n.node.__typename === 'Entity' &&
        n.node.stage === EntityStage.PreCreated
      ) {
        return false;
      }
      return true;
    }) ?? [];

  // Iterative orphan removal — hiding a node can leave downstream recipients
  // with no incoming edges within their section. Composite IDs (afterDeath:id)
  // scope the analysis per section. Nodes explicitly checked in the Edit
  // modal (Entity rows or ClientProfile directly-held-assets rows) are exempt
  // from the cascade.
  const allEdges = waterfall.visualizationWithProjections.edges ?? [];

  const nodesWithOriginalIncomingEdges = new Set<string>();
  for (const e of allEdges) {
    const fromId = getFilterNodeId(e.from);
    const toId = getFilterNodeId(e.to);
    if (fromId !== toId) {
      nodesWithOriginalIncomingEdges.add(toId);
    }
  }

  const nodeByCompositeId = hasUserFilter
    ? new Map<string, FilterableWaterfallNode>(
        visibleNodes.map((n) => [getFilterNodeId(n), n])
      )
    : null;

  const compositeNodeIds = new Set(visibleNodes.map((n) => getFilterNodeId(n)));
  let changed = true;
  while (changed) {
    changed = false;
    const nodesWithCurrentIncomingEdges = new Set<string>();
    for (const e of allEdges) {
      const fromId = getFilterNodeId(e.from);
      const toId = getFilterNodeId(e.to);
      if (
        fromId !== toId &&
        compositeNodeIds.has(fromId) &&
        compositeNodeIds.has(toId)
      ) {
        nodesWithCurrentIncomingEdges.add(toId);
      }
    }
    Array.from(compositeNodeIds).forEach((compositeId) => {
      if (
        !nodesWithOriginalIncomingEdges.has(compositeId) ||
        nodesWithCurrentIncomingEdges.has(compositeId)
      ) {
        return;
      }
      if (nodeByCompositeId) {
        // Invariant: compositeNodeIds is a subset of nodeByCompositeId keys.
        const node = nodeByCompositeId.get(compositeId)!;
        const typename = node.node.__typename;
        const isChecked =
          (typename === 'Entity' && checkedEntityIds.has(node.id)) ||
          (typename === 'ClientProfile' && checkedClientIds.has(node.id));
        if (isChecked) return;
      }
      compositeNodeIds.delete(compositeId);
      changed = true;
    });
  }

  const nodes = visibleNodes.filter((n) =>
    compositeNodeIds.has(getFilterNodeId(n))
  );
  const edges = allEdges.filter(
    (e) =>
      compositeNodeIds.has(getFilterNodeId(e.to)) &&
      compositeNodeIds.has(getFilterNodeId(e.from))
  );

  // Derive plain entity IDs for group filtering (groups reference nodes by entity ID only)
  const nodeIds = new Set(nodes.map((n) => n.id));

  // Exclude groups that don't have any visible nodes
  // Also filter out group nodes that are hidden
  const groups = (
    waterfall.visualizationGroups?.edges?.filter((e) => {
      const edgeNodeIds = e?.node?.nodes.map((n) => n.id) ?? [];
      const hasVisibleNodes = edgeNodeIds.some((id) => nodeIds.has(id));
      return hasVisibleNodes;
    }) ?? []
  ).reduce(
    (acc, group) => {
      const groupNodes =
        group?.node?.nodes.filter((n) => nodeIds.has(n.id)) ?? [];

      if (groupNodes.length) {
        acc?.push({
          ...group,
          node: {
            ...(group?.node ?? ({} as EstateWaterfall_GraphVizGroupFragment)),
            nodes: groupNodes,
          },
        });
      }

      return acc;
    },
    [] as {
      node?: {
        nodes: {
          id: string;
        }[];
      };
    }[]
  );

  // Return a new waterfall object with the filtered nodes, edges, and groups
  const wf: typeof waterfall = {
    ...waterfall,
    visualizationWithProjections: {
      ...waterfall.visualizationWithProjections,
      nodes,
      edges,
    },
    visualizationGroups: {
      edges: groups,
    },
  };

  return wf;
}

export function getWaterfallItemName(
  estateWaterfallVizNode:
    | GetWaterfallSummary_EstateWaterfallVizNodeFragment
    | EstateWaterfall_NodeFragment,
  nameOfDeadGrantor?: string
) {
  const lineOneAndTwoValues = {
    lineOne: EMPTY_CONTENT_HYPHEN,
    lineTwo: EMPTY_CONTENT_HYPHEN,
  };

  if (estateWaterfallVizNode.node.__typename === 'Entity') {
    lineOneAndTwoValues.lineOne =
      estateWaterfallVizNode.node.subtype.displayName;
    lineOneAndTwoValues.lineTwo =
      estateWaterfallVizNode.node.extendedDisplayKind;

    if (estateWaterfallVizNode.node.stage === EntityStage.Draft) {
      // This is a bit weird, but we want to remove any parentheticals from the
      // extended display kind that usually indicate the abbreviation, and then
      // add "(Draft)" to the end of it.
      lineOneAndTwoValues.lineTwo = `${estateWaterfallVizNode.node.extendedDisplayKind.replace(
        / *\([^)]*\) */g,
        ''
      )} (Draft)`;
    }
  }

  if (estateWaterfallVizNode.node.__typename === 'TestamentaryEntity') {
    lineOneAndTwoValues.lineOne = estateWaterfallVizNode.node.displayName;
    lineOneAndTwoValues.lineTwo = testamentaryEntityKindToDisplayName(
      estateWaterfallVizNode.node.testamentaryEntityKind,
      nameOfDeadGrantor
    );
  }

  if (estateWaterfallVizNode.node.__typename === 'ClientProfile') {
    lineOneAndTwoValues.lineOne = estateWaterfallVizNode.node.displayName;
  }

  if (estateWaterfallVizNode.node.__typename === 'ClientOrganization') {
    lineOneAndTwoValues.lineOne = estateWaterfallVizNode.node.name;
    lineOneAndTwoValues.lineTwo = formatEnumCase(
      estateWaterfallVizNode.node.clientOrganizationKind
    );
  }

  return lineOneAndTwoValues;
}
export function getSourceValues(
  data:
    | EstateWaterfallHypotheticalGratModalQuery
    | EstateWaterfallHypotheticalCharitableTrustModalQuery
): Record<string, SourceValues> {
  const entities = getNodes(data.sourceEntityOptions);
  const individuals =
    getNodes(data.households)[0]?.sourceIndividualOptions ?? [];
  const entitiesAndIndividuals = [...entities, ...individuals];
  const entityValues: Record<string, SourceValues> =
    entitiesAndIndividuals.reduce(
      (acc, entityOrIndividual) => {
        if (entityOrIndividual.__typename === 'ClientProfile') {
          const individualId = entityOrIndividual.id;
          acc[individualId] = {
            totalValue: new Decimal(0),
            taxDiscount: null,
            effectiveDiscountPercent: null,
            byClassId: {},
          };
        } else {
          const entityId = entityOrIndividual.id;
          acc[entityId] = {
            totalValue: new Decimal(0),
            taxDiscount: null,
            effectiveDiscountPercent: null,
            byClassId: {},
          };
        }

        return acc;
      },
      {} as Record<string, SourceValues>
    );

  entities.forEach((entity) => {
    const latestValuation =
      entity.subtype?.designerAccount?.valuations.edges?.[0]?.node;

    // Add asset class details
    latestValuation?.assets.edges?.forEach((edge) => {
      const asset = edge?.node;
      if (asset) {
        const classId = asset.class.id;
        if (!entityValues[entity.id]?.byClassId[classId]) {
          entityValues[entity.id]!.byClassId[classId] = {
            displayName: asset.class.displayName,
            totalValueForClass: new Decimal(0),
            hasOwnershipStake: null,
            ownershipPercentage: null,
            entityType: null,
            effectiveDiscountPercent: null,
          };
        }
        entityValues[entity.id]!.byClassId[classId]!.totalValueForClass =
          entityValues[entity.id]!.byClassId[classId]!.totalValueForClass.plus(
            asset.assetValue?.ownedValue ?? new Decimal(0)
          );
      }
    });

    // Add business entity details for entities
    entity.ownedOwnershipStakes?.forEach((stake) => {
      if (stake.ownedEntity) {
        entityValues[entity.id]!.byClassId[stake.ownedEntity.id] = {
          displayName: stake.ownedEntity.subtype.displayName,
          totalValueForClass: stake.ownedValue,
          hasOwnershipStake: Boolean(stake),
          ownershipPercentage: stake.ownershipPercentage ?? null,
          entityType:
            (stake.ownedEntity.subtype.__typename &&
              getEntityTypeFromSubtype(stake.ownedEntity.subtype.__typename)) ??
            null,
          effectiveDiscountPercent:
            stake.ownedEntity.subtype.effectiveDiscountPercent ?? null,
        };
      }
    });

    entityValues[entity.id]!.taxDiscount = entity.subtype?.taxDiscount ?? null;
    entityValues[entity.id]!.effectiveDiscountPercent =
      entity.subtype?.effectiveDiscountPercent ?? null;
  });

  individuals.forEach((individual) => {
    // Add business entity details for individuals
    individual.ownedOwnershipStakes?.forEach((stake) => {
      if (stake.ownedEntity) {
        entityValues[individual.id]!.byClassId[stake.ownedEntity.id] = {
          displayName: stake.ownedEntity.subtype.displayName,
          totalValueForClass: stake.ownedValue,
          hasOwnershipStake: Boolean(stake),
          ownershipPercentage: stake.ownershipPercentage ?? null,
          effectiveDiscountPercent:
            stake.ownedEntity.subtype.effectiveDiscountPercent ?? null,
          entityType:
            (stake.ownedEntity.subtype.__typename &&
              getEntityTypeFromSubtype(stake.ownedEntity.subtype.__typename)) ??
            null,
        };
      }
    });
  });

  Object.entries(entityValues).forEach(([entityId, value]) => {
    const totalValue = Object.values(value.byClassId).reduce(
      (acc, val) => acc.plus(val.totalValueForClass),
      new Decimal(0)
    );

    entityValues[entityId] = {
      totalValue,
      taxDiscount: value.taxDiscount,
      effectiveDiscountPercent: value.effectiveDiscountPercent,
      byClassId: value.byClassId,
    };
  });

  return entityValues;
}

export const inKindTransferToggleCopy = {
  label: 'Liquidate before transfer',
  helpText: `If enabled, the asset will be liquidated before transfer. The recipient’s custom growth rates will be applied instead of preserving the source asset’s growth assumptions.`,
};

export interface PendingGroupOps {
  newGroups: EstateWaterfall_GraphVizGroupFragment[];
  updatedGroups: EstateWaterfall_GraphVizGroupFragment[];
  deletedGroupIds: string[];
}

/**
 * Computes the pending group operations by diffing the original waterfall state
 * against the current waterfall state.
 *
 * @param originalWaterfall - The waterfall state when the trowser opened
 * @param currentWaterfall - The current waterfall state with pending changes
 * @returns Object containing arrays of new, updated, and deleted groups
 */
export function computePendingGroupOps(
  originalWaterfall: EstateWaterfallFragment | null,
  currentWaterfall: EstateWaterfallFragment
): PendingGroupOps {
  const originalGroups = originalWaterfall
    ? getNodes(originalWaterfall.visualizationGroups)
    : [];
  const currentGroups = getNodes(currentWaterfall.visualizationGroups);

  const originalGroupIds = new Set(originalGroups.map((g) => g.id));
  const currentGroupIds = new Set(currentGroups.map((g) => g.id));

  // New groups: in current but not in original (includes UI-only groups)
  const newGroups = currentGroups.filter((g) => {
    // UI-only groups are always new
    if (isUIOnlyGroupNode(g.id)) {
      return true;
    }
    // Defensive: real IDs not in original shouldn't happen in deferred mode
    if (!originalGroupIds.has(g.id)) {
      diagnostics.warn('Unexpected new group with real ID', { groupId: g.id });
      return true;
    }
    return false;
  });

  // Deleted groups: in original but not in current
  // Only include real (non-UI-only) groups since UI-only groups haven't been persisted yet
  const deletedGroupIds = originalGroups
    .filter((g) => !currentGroupIds.has(g.id) && !isUIOnlyGroupNode(g.id))
    .map((g) => g.id);

  // Updated groups: in both original and current, with changes
  const updatedGroups = currentGroups.filter((g) => {
    // Skip UI-only groups (they're new, not updates)
    if (isUIOnlyGroupNode(g.id)) {
      return false;
    }
    // Must exist in original
    if (!originalGroupIds.has(g.id)) {
      return false;
    }

    // just assume everything has changed for other groups,
    // rather than building a brittle diff heuristic
    return true;
  });

  return {
    newGroups,
    updatedGroups,
    deletedGroupIds,
  };
}

/**
 * Builds node configurations from React Flow nodes.
 * Extracts position, size, and metadata from each node.
 */
export function buildNodeConfigurations(
  nodes: Node[],
  existingNodeConfigurations: EstateWaterfallFragment['nodeConfigurations']
): EntityGraphNodeConfigurationInput[] {
  const newNodeConfigurations = nodes.flatMap((node) => {
    const {
      id: nodeID,
      position: { x: xPosition, y: yPosition },
      width,
      height,
      data,
      type,
    } = node;

    if (type === 'textNote') {
      return textNoteNodeToConfig(node);
    }

    if (type !== 'tile') return [];

    if (isNodeEligibleForGraveyard(node)) {
      if (!data.notes) return [];

      const graveyardConfig: EntityGraphNodeConfigurationInput = {
        nodeID,
        xPosition: null,
        yPosition: null,
        hidden: false,
        ...(typeof data.notes === 'string' && { notes: data.notes }),
        ...(data.componentType && { type: data.componentType }),
        ...(data.iconShape && { iconShape: data.iconShape }),
        ...(data.tileEmphasis && { tileEmphasis: data.tileEmphasis }),
      };
      return graveyardConfig;
    }

    return {
      nodeID,
      xPosition,
      yPosition,
      hidden: false,
      ...(height && { height }),
      ...(width && { width }),
      ...(data.fontSizeVariant && { fontSize: data.fontSizeVariant }),
      ...(typeof data.notes === 'string' &&
        data.notes && { notes: data.notes }),
      ...(data.componentType && { type: data.componentType }),
      ...(data.iconShape && { iconShape: data.iconShape }),
      ...(data.tileEmphasis && { tileEmphasis: data.tileEmphasis }),
      ...(data.dimensionsFinalized && { dimensionsFinalized: true }),
      ...(data.estateWaterfallConfig && {
        estateWaterfallConfig: data.estateWaterfallConfig,
      }),
    };
  });

  const existingConfigs: EntityGraphNodeConfigurationInput[] =
    existingNodeConfigurations?.flatMap((cfg) => {
      if (!cfg.notes) return [];
      // Text notes don't use the graveyard pattern (see getUpdateWaterfallVariables).
      if (isTextNoteId(cfg.nodeID)) return [];
      return {
        ...cfg,
        xPosition: null,
        yPosition: null,
      };
    }) ?? [];

  return uniqBy(
    [...newNodeConfigurations, ...existingConfigs],
    (c) => c.nodeID
  );
}

export interface BuildBatchUpdateInputArgs {
  waterfall: EstateWaterfallFragment;
  nodes: Node[];
  pendingOps: PendingGroupOps;
}

/**
 * Builds the input for the batchUpdateEstateWaterfall mutation.
 * Combines all diagram edit operations into a single atomic request.
 */
export function buildBatchUpdateInput({
  waterfall,
  nodes,
  pendingOps,
}: BuildBatchUpdateInputArgs): BatchUpdateEstateWaterfallInput {
  const nodeConfigurations = buildNodeConfigurations(
    nodes,
    waterfall.nodeConfigurations
  );

  const createGroups = pendingOps.newGroups
    .filter((group) => group.displayName && group.namespace)
    .map((group) => ({
      tempId: group.id,
      householdID: waterfall.household.id,
      displayName: group.displayName!,
      description: group.description ?? undefined,
      nodeIds: group.nodes.map((n) => n.id),
      namespace: group.namespace as AfterDeath,
    }));

  const updateGroups = pendingOps.updatedGroups.map((group) => ({
    id: group.id,
    displayName: group.displayName ?? undefined,
    description: group.description ?? undefined,
    nodeIds: group.nodes.map((n) => n.id),
  }));

  const {
    __typename,
    id: _vizConfigId,
    ...vizConfigRest
  } = waterfall.visualizationConfig ?? {};

  return {
    id: waterfall.id,
    nodeConfigurations,
    createGroups: createGroups.length > 0 ? createGroups : undefined,
    updateGroups: updateGroups.length > 0 ? updateGroups : undefined,
    deleteGroupIds:
      pendingOps.deletedGroupIds.length > 0
        ? pendingOps.deletedGroupIds
        : undefined,
    updateVisualizationConfig: vizConfigRest,
  };
}
